import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Двусторонняя связь URL-параметров с состоянием страницы.
 *
 * Любое поле страницы, которым мы хотим уметь делиться через ссылку
 * (фильтры, поиск, сортировка, открытая модалка) — описываем тут схемой.
 * Дефолтные значения в URL не пишутся, чтобы ссылки оставались короткими.
 *
 *   const SCHEMA = { q: '', status: 'all', sort: 'name', dir: 'asc', page: 1, student: null };
 *   const [params, setParams] = useUrlState(SCHEMA);
 *   // params.q, params.status, ...
 *   setParams({ status: 'archive', q: 'иван' });   // обновит ?status=archive&q=иван
 *   setParams({ status: 'all' });                  // удалит status из URL (это дефолт)
 *
 * ВАЖНО: схему передавать стабильной ссылкой — определять вне компонента
 * либо обёрнутой в useMemo. Иначе будут лишние ререндеры.
 *
 * @template {Record<string, string|number|boolean|null>} S
 * @param {S} schema  объект ключ→дефолтное значение (определяет тип)
 * @returns {[S, (patch: Partial<S>) => void]}
 */
export function useUrlState(schema) {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo(() => {
    const out = {};
    for (const key in schema) {
      const def = schema[key];
      const raw = searchParams.get(key);
      if (raw === null) {
        out[key] = def;
      } else if (typeof def === 'number') {
        const n = Number(raw);
        out[key] = Number.isFinite(n) ? n : def;
      } else if (typeof def === 'boolean') {
        out[key] = raw === '1' || raw === 'true';
      } else {
        out[key] = raw;
      }
    }
    return out;
  }, [searchParams, schema]);

  const setParams = useCallback((patch) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const key in patch) {
        const value = patch[key];
        const def = schema[key];
        const isDefault =
          value === def ||
          value === null ||
          value === undefined ||
          value === '' ||
          (typeof def === 'number' && Number(value) === Number(def));
        if (isDefault) {
          next.delete(key);
        } else if (typeof value === 'boolean') {
          next.set(key, value ? '1' : '0');
        } else {
          next.set(key, String(value));
        }
      }
      return next;
    }, { replace: true });
  }, [setSearchParams, schema]);

  return [params, setParams];
}
